import classNames from "classnames";

import CopyToClipboardButton from "@/shared/components/CopyToClipboardButton";
import { FileMetadata } from "@/shared/components/FileMetadataAndTopics/FileMetadata";
import { UpdateFileTags } from "@/shared/components/FileMetadataAndTopics/UpdateFileTags";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal/RobotoPrincipalUnderlineLink.tsx";
import { FileRecord } from "@/shared/domain/files";
import { basename, dirname } from "@/shared/domain/files/filesystem";
import { EventName, EventProps } from "@/shared/services/analytics";
import { formatByteSize } from "@/shared/text";

import styles from "./FileDetails.module.css";

interface FileDetailsProps {
  fileRecord: FileRecord;
}

export function FileDetails({ fileRecord }: FileDetailsProps) {
  const filename = basename(fileRecord.relative_path);
  const directory = dirname(fileRecord.relative_path);
  const showRelativePath = directory && directory !== "." && directory !== "/";

  return (
    <div className={styles.container}>
      <div className={styles.main}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>Filename</div>
          <CopyableValue text={filename} />
        </div>

        {showRelativePath && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>Relative Path</div>
            <CopyableValue text={fileRecord.relative_path} />
          </div>
        )}

        <div className={styles.section}>
          <div className={styles.sectionHeader}>Tags</div>
          <UpdateFileTags fileId={fileRecord.file_id} />
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>Metadata</div>
          <FileMetadata fileId={fileRecord.file_id} />
        </div>
      </div>

      <div className={styles.rail}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>File Size</div>
          <div className={styles.sectionContent}>
            {fileRecord.size !== undefined && formatByteSize(fileRecord.size)}
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>Created</div>
          <div className={styles.sectionContent}>
            {fileRecord.created !== undefined &&
              new Date(fileRecord.created).toLocaleString()}
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>Last Modified</div>
          <div className={styles.sectionContent}>
            {fileRecord.modified !== undefined &&
              new Date(fileRecord.modified).toLocaleString()}
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>Source</div>
          <div className={styles.sectionContent}>
            <RobotoPrincipalUnderlineLink
              principal={fileRecord.created_by}
              icon
            />
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>File ID</div>
          <CopyableValue
            text={fileRecord.file_id}
            eventName="CopyToClipboardClicked"
            eventProps={{ link: fileRecord.file_id }}
          />
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>Dataset ID</div>
          <CopyableValue text={fileRecord.association_id} />
        </div>

        {fileRecord.device_id && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>Device ID</div>
            <CopyableValue text={fileRecord.device_id} />
          </div>
        )}
      </div>
    </div>
  );
}

interface CopyableValueProps {
  text: string;
  eventName?: EventName;
  eventProps?: EventProps<EventName>;
}

function CopyableValue({ text, eventName, eventProps }: CopyableValueProps) {
  return (
    <div className={classNames(styles.sectionContent, styles.copyableContent)}>
      <span className={styles.copyableText}>{text}</span>
      <CopyToClipboardButton
        text={text}
        eventName={eventName}
        eventProps={eventProps}
      />
    </div>
  );
}
